// Pure column sorter for the Sequential milestone table view.
// Status order matches the lifecycle precedence so DESC reads as
// "latest in lifecycle first"; ASC reads as "earliest in lifecycle first".

import type { Milestone } from "../../../../types";
import { deriveMilestoneDisplayLifecycle } from "../../../../utils/deriveMilestoneDisplayLifecycle";

export type MilestoneSortKey =
  | "number"
  | "name"
  | "progress"
  | "value"
  | "status";

export type MilestoneSortDir = "asc" | "desc";

const LIFECYCLE_ORDER: Record<string, number> = {
  upcoming: 0,
  delivered: 1,
  "partially-accepted": 2,
  accepted: 3,
  paid: 4,
};

/** Order by the numeric backbone first, then the optional insertion letter,
 *  so 13 < 13a < 13b < 14. A suffix-less milestone sorts before its lettered
 *  siblings ("" < "a"). Exported as the single source of register order —
 *  any surface listing milestones "in register order" (the sequential
 *  table, the finance tag pickers) should sort with this. */
export function compareMilestoneNumber(
  a: Pick<Milestone, "milestoneNumber" | "milestoneSuffix">,
  b: Pick<Milestone, "milestoneNumber" | "milestoneSuffix">,
): number {
  return (
    a.milestoneNumber - b.milestoneNumber ||
    (a.milestoneSuffix ?? "").localeCompare(b.milestoneSuffix ?? "")
  );
}

function compareForKey(a: Milestone, b: Milestone, key: MilestoneSortKey): number {
  switch (key) {
    case "number":
      return compareMilestoneNumber(a, b);
    case "name":
      return a.name.localeCompare(b.name);
    case "value":
      return (a.paymentAmount ?? 0) - (b.paymentAmount ?? 0);
    case "progress":
    case "status": {
      const aOrder = LIFECYCLE_ORDER[deriveMilestoneDisplayLifecycle(a)] ?? 0;
      const bOrder = LIFECYCLE_ORDER[deriveMilestoneDisplayLifecycle(b)] ?? 0;
      return aOrder - bOrder;
    }
  }
}

export function sortMilestones(
  items: readonly Milestone[],
  key: MilestoneSortKey,
  dir: MilestoneSortDir,
): Milestone[] {
  const copy = [...items];
  copy.sort((a, b) => {
    const primary = compareForKey(a, b, key);
    if (primary !== 0) return dir === "asc" ? primary : -primary;
    return compareMilestoneNumber(a, b);
  });
  return copy;
}
